import React from 'react';

import { BarChart } from '@mui/x-charts/BarChart';
import moment from 'moment';

import { MISC_DATE_ISO_FORMAT } from '../../../../core/constants';
import { truncateLabel, useChartColors } from '../../sales_analysis/sales_analysis_utils';
import ChartCard from '../../sales_analysis/chart_card';

const LIMIT = 10;
// below this share of elapsed shelf life a slower sale is not a risk yet
const MIN_TIME_PERCENTAGE = 50;
const ROW_HEIGHT = 44;

const percentFormatter = value => `${ value }%`;

// a batch is at risk when most of its shelf life has passed and it sells slower than time passes
const SellThroughChart = ({ businessData }) => {
    const chartColors = useChartColors();
    const dataset = (businessData || [])
        .filter(resource => resource?.numberOfStockedItems - resource?.numberOfSoldItems > 0)
        .map(resource => ({
            productName: resource?.productName,
            batch: resource?.productBatchId,
            stockingDate: moment(resource?.stockingDate, MISC_DATE_ISO_FORMAT).format('DD.MM HH:mm'),
            salePercentage: Number(resource?.salePercentage || 0),
            timePercentage: Number(resource?.timePercentage || 0)
        }))
        .filter(row => row.timePercentage >= MIN_TIME_PERCENTAGE && row.timePercentage > row.salePercentage)
        .sort((a, b) => (b.timePercentage - b.salePercentage) - (a.timePercentage - a.salePercentage))
        .slice(0, LIMIT)
        // the same batch can be stocked several times, so rows are keyed by position instead of by label
        .map((row, index) => ({ ...row, key: index }));

    const tickLabel = key => `${ dataset[key]?.productName } (${ dataset[key]?.batch })`;
    const tooltipLabel = key => `${ tickLabel(key) } · zatowarowano ${ dataset[key]?.stockingDate }`;

    return (
        <ChartCard
            title="Partie zagrożone przeterminowaniem"
            subtitle={ `Do ${ LIMIT } partii po upływie co najmniej ${ MIN_TIME_PERCENTAGE }% terminu, w których sprzedaż nie nadąża za upływem terminu` }
            empty={ dataset.length === 0 }
            emptyMessage="Brak partii zagrożonych przeterminowaniem"
            height={ dataset.length === 0 ? 120 : dataset.length * ROW_HEIGHT + 96 }
        >
            {
                dataset.length > 0 && (
                    <BarChart
                        layout="horizontal"
                        dataset={ dataset }
                        series={ [
                            { dataKey: 'salePercentage', label: 'Sprzedano', color: chartColors.primary, valueFormatter: percentFormatter },
                            { dataKey: 'timePercentage', label: 'Upłynęło terminu', color: chartColors.secondary, valueFormatter: percentFormatter }
                        ] }
                        yAxis={ [{ scaleType: 'band', dataKey: 'key', valueFormatter: (key, context) => context.location === 'tick' ? truncateLabel(tickLabel(key), 32) : tooltipLabel(key) }] }
                        xAxis={ [{ min: 0, valueFormatter: percentFormatter, tickNumber: 5 }] }
                        grid={ { vertical: true } }
                        borderRadius={ 4 }
                        margin={ { left: 250, right: 24, top: 48, bottom: 32 } }
                        slotProps={ { legend: { position: { vertical: 'top', horizontal: 'right' } } } }
                    />
                )
            }
        </ChartCard>
    );
};

export default SellThroughChart;
